<template>
  <div id="app" class="h-100">

    <template v-if="isAuth">
      <Intro />
      <ChatBot />
    </template>

    <component :is="layout">
      <router-view />
    </component>

    <template v-if="isAuth">
      <GlobalAddTaskModal />
    </template>
  </div>
</template>


<script>
// This will be populated in `beforeCreate` hook
import GlobalAddTaskModal from "./views/admin/task-management/GlobalAddTaskModal.vue";
import ChatBot from "./views/admin/chatbot/ChatBot.vue";
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

import { useWindowSize, useCssVar } from "@vueuse/core";
import Intro from "@/layouts/components/Intro.vue"

import { requestNotificationPermission, onMessage, messaging } from './firebase';

import store from "@/store";
import { mapGetters } from "vuex";
const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutSuperAdmin = () => import("@/layouts/super-admin/LayoutSuperAdmin.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    GlobalAddTaskModal,
    Intro,
    LayoutSuperAdmin,
    ChatBot,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    ...mapGetters({
      isAuth: "authModule/getIsAuthenticated",
      isSuperAdmin: "superAdminAuthModule/getIsAuthenticated",
    }),
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      if (this.$route.meta.layout === "super-admin") return "layout-super-admin";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
  },

  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },

  mounted() {

    if (this.isAuth || this.isSuperAdmin){
      this.ensureFcmToken()
    }

    onMessage(messaging, (payload) => {
      console.log("Message received in foreground: ", payload);
      const { title, body } = payload.data;

      new Notification(title, {
        body,
        icon: "/img/icons/android/android-launchericon-72-72.png"
      });
    });

  },

  setup() {
    const { skin, skinClasses } = useAppConfig();

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses,
    };
  },

  methods: {
    async ensureFcmToken() {
      let token = localStorage.getItem('fcm_token');

      if (!token) {
        token = await requestNotificationPermission();
        if (token) {
          localStorage.setItem('fcm_token', token);

          try {
            await this.$api.post('/api/save-fcm-token', {
              token,
              device_info: navigator.userAgent,
              is_pwa: window.matchMedia('(display-mode: standalone)').matches
            });
            console.log('Token sent to backend');
          } catch (e) {
            console.error('Error sending token to backend', e);
          }
        } else {
          console.warn('No token received');
        }
      }

      console.log('getting token')

      return token;
    }

  }
};
</script>


